import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  X, 
  Tags, 
  Send, 
  RefreshCw, 
  Power, 
  Download,
  Trash2,
  CheckCircle,
  AlertCircle,
  Lock,
  Unlock,
  Loader2
} from "lucide-react";
import { type Device } from "@/hooks/useDevices";
import { toast } from "sonner";
import * as devicesApi from "@/services/devicesApi";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagRemovalDialog } from "@/components/tags/TagRemovalDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface BulkActionToolbarProps {
  selectedDevices: Device[];
  onClearSelection: () => void;
  onRefresh?: () => void;
}

export const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedDevices,
  onClearSelection,
  onRefresh
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLockDialog, setShowLockDialog] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [showCheckInDialog, setShowCheckInDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);
  const [isTagRemovalDialogOpen, setIsTagRemovalDialogOpen] = useState(false);

  const handleBulkTagAssignment = () => {
    setIsTagDialogOpen(true);
  };

  const handleBulkTagRemoval = () => {
    setIsTagRemovalDialogOpen(true);
  };

  const handleBulkCheckIn = async () => {
    setIsLoading(true);
    try {
      const results = await Promise.allSettled(
        selectedDevices.map(d => devicesApi.checkInDevice({ uuid: d.id }))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;
      if (failed === 0) {
        toast.success(`Check-in command sent to ${succeeded} devices`);
      } else {
        toast.warning(`Check-in sent to ${succeeded} devices, ${failed} failed`);
      }
      onRefresh?.();
    } catch (error) {
      console.error('Bulk check-in error:', error);
      toast.error('Failed to check in devices');
    } finally {
      setIsLoading(false);
      setShowCheckInDialog(false);
    }
  };

  const handleBulkLock = async () => {
    setIsLoading(true);
    try {
      const deviceIds = selectedDevices.map(d => d.id);
      await devicesApi.bulkLock({ deviceIds });
      toast.success(`Lock command sent to ${selectedDevices.length} devices`);
      onRefresh?.();
    } catch (error) {
      console.error('Bulk lock error:', error);
      toast.error('Failed to lock devices');
    } finally {
      setIsLoading(false);
      setShowLockDialog(false);
    }
  };

  const handleBulkUnlock = async () => {
    setIsLoading(true);
    try {
      const deviceIds = selectedDevices.map(d => d.id);
      await devicesApi.bulkUnlock({ deviceIds });
      toast.success(`Unlock command sent to ${selectedDevices.length} devices`);
      onRefresh?.();
    } catch (error) {
      console.error('Bulk unlock error:', error);
      toast.error('Failed to unlock devices');
    } finally {
      setIsLoading(false);
      setShowUnlockDialog(false);
    }
  };

  const handleBulkUnenroll = async () => {
    setIsLoading(true);
    try {
      const deviceIds = selectedDevices.map(d => d.id);
      await devicesApi.bulkUnenroll({ deviceIds });
      toast.success(`${selectedDevices.length} devices unenrolled successfully`);
      onClearSelection();
      onRefresh?.();
    } catch (error) {
      console.error('Bulk unenroll error:', error);
      toast.error('Failed to unenroll devices');
    } finally {
      setIsLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleExport = () => {
    // Create CSV data
    const csvData = selectedDevices.map(device => ({
      name: device.name,
      status: device.status,
      os_type: device.os_type,
      os_version: device.os_version,
      department: device.department || 'Unassigned',
      location: device.location || 'Unknown',
      customer: device.customer || 'Not Available',
      last_seen: device.last_seen || device.created_at
    }));

    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `devices-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast.success(`Exported ${selectedDevices.length} devices to CSV`);
  };

  return (
    <>
      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="px-3 py-1">
            {selectedDevices.length} selected
          </Badge>
          <Separator orientation="vertical" className="h-6" />
          
          {/* Quick Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCheckInDialog(true)}
              disabled={isLoading}
              className="h-8"
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Check In
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkTagAssignment}
              className="h-8"
            >
              <Tags className="mr-2 h-4 w-4" />
              Assign Tags
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkTagRemoval}
              className="h-8"
            >
              <X className="mr-2 h-4 w-4" />
              Remove Tags
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowLockDialog(true)}
              disabled={isLoading}
              className="h-8"
            >
              <Lock className="mr-2 h-4 w-4" />
              Lock
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUnlockDialog(true)}
              disabled={isLoading}
              className="h-8"
            >
              <Unlock className="mr-2 h-4 w-4" />
              Unlock
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8">
                  More Actions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuLabel>Bulk Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                <DropdownMenuItem onClick={handleExport}>
                  <Download className="mr-2 h-4 w-4" />
                  Export to CSV
                </DropdownMenuItem>
                
                <DropdownMenuSeparator />
                
                <DropdownMenuItem 
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Power className="mr-2 h-4 w-4" />
                  Unenroll Devices
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="h-8 w-8 p-0"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Unenroll Selected Devices
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unenroll {selectedDevices.length} device(s)? This action cannot be undone and will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Remove the devices from management</li>
                <li>Revoke all applied configurations</li>
                <li>Clear device enrollment data</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkUnenroll}
              disabled={isLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Unenrolling...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Unenroll Devices
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showLockDialog} onOpenChange={setShowLockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-warning" />
              Lock Selected Devices
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to lock {selectedDevices.length} device(s)? The devices will be remotely locked and require a passcode to unlock.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkLock}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Locking...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Lock Devices
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Unlock className="h-5 w-5" />
              Unlock Selected Devices
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlock {selectedDevices.length} device(s)? The devices will be remotely unlocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkUnlock}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Unlocking...
                </>
              ) : (
                <>
                  <Unlock className="mr-2 h-4 w-4" />
                  Unlock Devices
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showCheckInDialog} onOpenChange={setShowCheckInDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Check In Selected Devices
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to trigger a check-in for {selectedDevices.length} device(s)? This will request each device to report its current status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkCheckIn}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking In...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Check In Devices
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TagAssignmentDialog
        open={isTagDialogOpen}
        onOpenChange={setIsTagDialogOpen}
        resourceIds={selectedDevices.map(d => d.id)}
        resourceType="device"
        resourceName={`${selectedDevices.length} devices`}
        onAssignmentComplete={() => {
          setIsTagDialogOpen(false);
          onRefresh?.();
          toast.success(`Tags assigned to ${selectedDevices.length} devices`);
        }}
      />

      <TagRemovalDialog
        open={isTagRemovalDialogOpen}
        onOpenChange={setIsTagRemovalDialogOpen}
        deviceIds={selectedDevices.map(d => d.id)}
        deviceName={`${selectedDevices.length} devices`}
        onRemovalComplete={() => {
          setIsTagRemovalDialogOpen(false);
          onRefresh?.();
        }}
      />
    </>
  );
};